import BadgeIcon from "components/badgeIcon";
import Typography from "components/typography";
import { useClickOutside } from "hooks/UseClickOutside";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { useDispatch } from "react-redux";
import { setActiveArtist } from "../../../redux/features/activeArtist/index";
import { GetActiveArtist, GetUserArtists } from "services/user/getUser";
import { IUserArtist } from "types/user";
import { shortenText } from "utils/shorten-text";

const SelectArtist = () => {
  const [caretOpen, setCaretOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useClickOutside(ref, () => setCaretOpen(false));

  const BgColors = [Colors.yellow400, Colors.purple100];

  const [artists, setArtists] = useState<IUserArtist[]>();

  const Artists = useMemo(() => GetUserArtists(), []);

  const activeArtist = GetActiveArtist();

  useEffect(() => {
    if (Artists) {
      setArtists(Artists);
    }
  }, [Artists]);

  const SetArtist = (artistId: string) => {
    dispatch(setActiveArtist(artistId));
  };

  const CreatNewArtistBtn = () => {
    return (
      <ArtistItem
        isactive="false"
        onClick={() => navigate("/artists?createNew=true")}
      >
        <Icon bgColor={Colors.grey500}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.00078 1.7998C6.15991 1.7998 6.31252 1.86302 6.42505 1.97554C6.53757 2.08806 6.60078 2.24067 6.60078 2.3998V5.3998H9.60078C9.75991 5.3998 9.91252 5.46302 10.025 5.57554C10.1376 5.68806 10.2008 5.84067 10.2008 5.9998C10.2008 6.15893 10.1376 6.31155 10.025 6.42407C9.91252 6.53659 9.75991 6.5998 9.60078 6.5998H6.60078V9.5998C6.60078 9.75893 6.53757 9.91155 6.42505 10.0241C6.31252 10.1366 6.15991 10.1998 6.00078 10.1998C5.84165 10.1998 5.68904 10.1366 5.57652 10.0241C5.464 9.91155 5.40078 9.75893 5.40078 9.5998V6.5998H2.40078C2.24165 6.5998 2.08904 6.53659 1.97652 6.42407C1.864 6.31155 1.80078 6.15893 1.80078 5.9998C1.80078 5.84067 1.864 5.68806 1.97652 5.57554C2.08904 5.46302 2.24165 5.3998 2.40078 5.3998H5.40078V2.3998C5.40078 2.24067 5.464 2.08806 5.57652 1.97554C5.68904 1.86302 5.84165 1.7998 6.00078 1.7998Z"
              fill="#1E1E1E"
            />
          </svg>
        </Icon>
        <Typography weight="medium" size="s" color={Colors.white100} ml="0.8">
          Create New Artist
        </Typography>
      </ArtistItem>
    );
  };

  return (
    <Container ref={ref}>
      {activeArtist ? (
        <Main
          onClick={() => {
            activeArtist ? setCaretOpen(!caretOpen) : navigate("/artists");
          }}
        >
          <BadgeIcon
            name={activeArtist?.name}
            fontSize="lg"
            bgColor={Colors.yellow400}
            size="3.2"
          />
          <Typography ml="1.2" size="xl" weight="medium" mr="1.2">
            {activeArtist?.name}
          </Typography>
          <Caret open={caretOpen ? "true" : "false"}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M12.6654 6L7.9987 10L3.33203 6"
                stroke="white"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Caret>
        </Main>
      ) : (
        <CreatNewArtistBtn />
      )}

      <ArtistsContainer isopen={caretOpen ? "true" : "false"}>
        {artists?.map(({ id, name }, i) => {
          return (
            <ArtistItem
              isactive={activeArtist?.id === id ? "true" : "false"}
              onClick={() => {
                caretOpen && SetArtist(id);
              }}
              key={i}
            >
              <BadgeIcon
                name={name}
                fontSize="lg"
                bgColor={BgColors[i]}
                size="2.4"
              />
              <Typography weight="medium" color={Colors.white100} ml="0.8">
                {shortenText(name, 10)}
              </Typography>
            </ArtistItem>
          );
        })}
        <CreatNewArtistBtn />
      </ArtistsContainer>
    </Container>
  );
};

export default SelectArtist;

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 0.8rem;
  cursor: pointer;
  position: relative;
`;
const Main = styled.div`
  display: flex;
  align-items: center;
  padding: 0.8rem;
  cursor: pointer;
  position: relative;
`;
const Icon = styled.div<{ bgColor: string; size?: string }>`
  width: ${(props) => (props.size ? props.size : "2.4rem")};
  height: ${(props) => (props.size ? props.size : "2.4rem")};
  border-radius: 50%;
  background-color: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Caret = styled.div<{ open: string }>`
  width: 1.6rem;
  transform: ${(props) => (props.open === "true" ? "rotate(180deg)" : "")};
  transition: ease-in-out all 0.2s;
  position: relative;
`;

const ArtistsContainer = styled.div<{ isopen: string }>`
  position: absolute;
  width: fit-content;
  width: 16rem;
  background-color: ${Colors.grey1000};
  padding: 0.6rem;
  top: calc(100% + 1rem);
  border-radius: 0.8rem;
  z-index: 5;
  opacity: ${(props) => (props.isopen === "true" ? "1" : "0")};
  transform: ${(props) =>
    props.isopen === "true" ? "translateY(0%)" : "translateY(-150%)"};
  transition: all ease-in-out 0.15s;
`;

const ArtistItem = styled.div<{ isactive: string }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 1.2rem 0.7rem;
  border-radius: 0.8rem;
  background-color: ${(props) =>
    props.isactive === "true" ? Colors.grey700 : "transparent"};
  margin-bottom: 0.4rem;
`;
